import { z } from 'zod/v4'
import type { OrganizationTemplate } from '@skop-app/lib-domain/types'

import type {
  CatalogBlock,
  CatalogHeaderBlock,
  CatalogItemsParams,
  CatalogProductBlock,
  HeaderItem,
} from '@/types/catalog'
import type { AdaptedProduct } from '@/services/property'
import type { Context } from '@/types/grid'
import { formulaOverride } from './formula.helpers'
import { matchKey } from './matchKey.utils'
import { getAdaptedField } from './value.utils'
import { buildSyntheticHeaderId } from './catalog.syntheticHeaderId'
import {
  buildRotorPdfPages,
  estimateRotorItemHeightCm,
  paginateRotorEntries,
} from './catalog.rotorLayout'

import { extractValue, getField } from './product.handler'

export const serializeCatalogBlocks = (blocks: CatalogBlock[]) =>
  blocks.map((block) => ({
    ...block,
    data:
      block.type === 'HEADER'
        ? JSON.stringify(block.data)
        : JSON.stringify({ detailedSheet: block.data.detailedSheet }),
  }))

// Summary pagination: the first page contains the branding block while
// following pages can use almost the full vertical space.
const SUMMARY_FIRST_PAGE_AVAILABLE_HEIGHT = 19.8
const SUMMARY_NEXT_PAGE_AVAILABLE_HEIGHT = 25.8
const SUMMARY_ENTRY_BASE_HEIGHT = 1.55
const SUMMARY_ENTRY_EXTRA_LINE_HEIGHT = 0.48
const SUMMARY_TITLE_CHARS_PER_LINE = 42

type SkopSection = {
  name: string
  quantity: number
  items: AdaptedProduct[]
  pageNumber?: number
}

type RotorCategorySection = {
  category: string
  items: Array<HeaderItem | AdaptedProduct>
}

type RotorLotSection = {
  lot: string
  categories: RotorCategorySection[]
}

/**
 * Estimates the rendered height of any catalog page item (header or product) in cm.
 * Used by pagination functions to determine how many items fit on a page.
 */
const estimateItemHeight = (item: HeaderItem | AdaptedProduct): number => {
  return estimateRotorItemHeightCm(item)
}

const estimateSummaryTitleLines = (title: string): number =>
  title
    .split('\n')
    .reduce(
      (sum, line) =>
        sum +
        Math.max(
          1,
          Math.ceil(line.trim().length / SUMMARY_TITLE_CHARS_PER_LINE),
        ),
      0,
    )

const estimateSummaryEntryHeight = (category: Pick<SkopSection, 'name'>) => {
  const titleLines = estimateSummaryTitleLines(category.name)
  return (
    SUMMARY_ENTRY_BASE_HEIGHT +
    Math.max(0, titleLines - 1) * SUMMARY_ENTRY_EXTRA_LINE_HEIGHT
  )
}

const getSummaryPageAvailableHeight = (pageIndex: number) =>
  pageIndex === 0
    ? SUMMARY_FIRST_PAGE_AVAILABLE_HEIGHT
    : SUMMARY_NEXT_PAGE_AVAILABLE_HEIGHT

export const paginateSkopSummary = (
  categories: SkopSection[],
): SkopSection[][] => {
  const pages: SkopSection[][] = [[]]
  let pageIndex = 0
  let remainingHeight = getSummaryPageAvailableHeight(pageIndex)

  categories.forEach((category) => {
    const entryHeight = estimateSummaryEntryHeight(category)

    if (pages[pageIndex].length > 0 && entryHeight > remainingHeight) {
      pageIndex += 1
      pages.push([])
      remainingHeight = getSummaryPageAvailableHeight(pageIndex)
    }

    pages[pageIndex].push(category)
    remainingHeight = Math.max(0, remainingHeight - entryHeight)
  })

  return pages
}

const getSkopSummaryPageCount = (categories: SkopSection[]) =>
  paginateSkopSummary(categories).length

const resolveCatalogLotOption = (
  header: CatalogHeaderBlock,
  context?: Context,
) => {
  const categoryOptions = context?.propertyOptions?.category ?? []
  const explicitLotValue = extractValue(header.data.lot)

  if (explicitLotValue) {
    const matchedLot = matchKey(
      explicitLotValue,
      categoryOptions,
      context?.optionsByID?.category,
    )
    if (matchedLot) {
      const resolvedValue =
        matchedLot.id ?? matchedLot.value?.toString() ?? matchedLot.label
      if (resolvedValue) {
        return {
          identity: matchedLot.id ?? resolvedValue,
          value: resolvedValue,
        }
      }
    }

    return {
      identity: explicitLotValue,
      value: explicitLotValue,
    }
  }

  const categoryValue = extractValue(header.data.category)
  if (!categoryValue) return null

  const matchedLot = categoryOptions.find((lot) =>
    lot.options?.some(
      (option) =>
        option.id === categoryValue ||
        option.label === categoryValue ||
        option.value?.toString() === categoryValue,
    ),
  )

  if (!matchedLot) return null

  const resolvedValue =
    matchedLot.id ?? matchedLot.value?.toString() ?? matchedLot.label
  if (!resolvedValue) return null

  return {
    identity: matchedLot.id ?? resolvedValue,
    value: resolvedValue,
  }
}

export const getDisplayLotForHeader = (
  blocks: CatalogBlock[],
  headerId: string,
  context?: Context,
) => {
  let currentLotIdentity: string | undefined

  for (const block of blocks) {
    if (block.type !== 'HEADER') continue

    const resolvedLot = resolveCatalogLotOption(block, context)
    const shouldDisplayLot = Boolean(
      resolvedLot?.identity && resolvedLot.identity !== currentLotIdentity,
    )

    currentLotIdentity = resolvedLot?.identity

    if (block.id !== headerId) continue
    if (!shouldDisplayLot) return undefined
    return resolvedLot?.value
  }

  return undefined
}

export const normalizeCatalogLotHeaders = (
  blocks: CatalogBlock[],
  context?: Context,
): CatalogBlock[] =>
  blocks.map((block) => {
    if (block.type !== 'HEADER') return block

    const displayLot = getDisplayLotForHeader(blocks, block.id, context)

    if (!displayLot && !block.data.lot) return block
    if (displayLot && extractValue(block.data.lot) === displayLot) return block

    const nextData = { ...block.data }
    if (displayLot) {
      nextData.lot = displayLot
    } else {
      delete nextData.lot
    }

    return {
      ...block,
      data: nextData,
    }
  })

const readQuantityNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    const parsed = Number(trimmed.replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : undefined
  }

  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    const nestedCandidates = [record.number, record.value, record.label]
    for (const candidate of nestedCandidates) {
      const parsed = readQuantityNumber(candidate)
      if (parsed !== undefined) return parsed
    }
  }

  return undefined
}

const getProductQuantityNumber = (
  product: AdaptedProduct,
): number | undefined => {
  const directQuantity = (product as Record<string, unknown>).quantity
  if (
    typeof directQuantity === 'object' &&
    directQuantity !== null &&
    'number' in directQuantity
  ) {
    const directNumber = readQuantityNumber(
      (directQuantity as { number?: unknown }).number,
    )
    if (directNumber !== undefined) return directNumber
  }

  const quantityField = getField(product, 'quantity')
  if (
    typeof quantityField === 'object' &&
    quantityField !== null &&
    'number' in quantityField
  ) {
    const nestedNumber = readQuantityNumber(
      (quantityField as { number?: unknown }).number,
    )
    if (nestedNumber !== undefined) return nestedNumber
  }

  return readQuantityNumber(product?.['quantity.number']?.value)
}

const sumQuantities = (products: AdaptedProduct[]): number | undefined => {
  let hasQuantity = false
  const total = products.reduce((acc, item) => {
    const quantity = getProductQuantityNumber(item)
    if (quantity === undefined) return acc
    hasQuantity = true
    return acc + quantity
  }, 0)

  if (!hasQuantity) return undefined
  return Math.round((total + Number.EPSILON) * 100) / 100
}

const getQuantities = (products: AdaptedProduct[]) =>
  sumQuantities(products) ?? 0

export const getNbrOfItemsPerPage = (template: OrganizationTemplate) => {
  switch (template) {
    case 'references':
      return 7
    case 'dense':
      return 5
    case 'rotor':
      return 4
    default:
      return 1
  }
}

const normalizeTemplate = (value: unknown): OrganizationTemplate => {
  if (value === 'references' || value === 'dense' || value === 'rotor') {
    return value
  }
  return 'references'
}

export const getByMetaValue = (product: AdaptedProduct, meta: string) => {
  const key = Object.keys(product).find((fieldKey) => {
    const field = getAdaptedField(product, fieldKey)
    if (!field) return false
    return Boolean((field as Record<string, unknown>)[meta])
  })
  const value = key ? getAdaptedField(product, key)?.label : undefined
  return value && (formulaOverride(value) || value)
}

export const getRotorCustomLabel = (product: AdaptedProduct) => {
  const id = getByMetaValue(product, 'isID')
  const ref = product?.projectID?.label?.slice(0, 10)
  return ref && id && `${ref}-${id}`
}

export const getReducedProducts = (
  products: AdaptedProduct[],
): AdaptedProduct[] =>
  products.reduce((acc, product) => {
    if (
      // !product?.batchedProductID && // Not a batched product, or a parent one
      !acc.some((p) => product.productID === p.productID) // Not already in the list
    ) {
      // Occurrences of the product (same productID)
      const occurrences = products.filter(
        (occurrence) => occurrence.productID === product.productID,
      )
      // Parent product
      if (occurrences.length > 1) {
        // Quantity of the product or sum of quantities of occurrences
        const number = sumQuantities(occurrences)

        // Shapes of the product or array of shapes from occurrences
        // Only include shapes from occurrences that have a defined quantity
        const shapeID = occurrences.reduce(
          (acc, occurrence) => {
            const hasQuantity =
              getProductQuantityNumber(occurrence) !== undefined
            if (!hasQuantity) return acc

            const shapeValues = occurrence?.shapeID ?? []
            shapeValues.forEach((shapeValue) => {
              if (!acc.some((shape) => shape.label === shapeValue.label)) {
                acc.push({
                  label: shapeValue.label,
                  value: shapeValue.value,
                })
              }
            })
            return acc
          },
          [] as Array<{ label: string; value: string }>,
        )

        // Channels of the product or array of channels from occurrences
        const channels = occurrences.reduce(
          (acc, occurrence) => {
            if (occurrence?.channels?.length)
              occurrence.channels.map((channel) => {
                if (!acc.some((c) => c.label === channel.label))
                  acc.push({
                    label: channel.label,
                    value: channel.value,
                  })
              })
            return acc
          },
          [] as Array<{ label: string; value: string }>,
        )

        const result: AdaptedProduct = {
          ...product,
          batchedProducts: occurrences,
          shapeID,
          channels,
        }

        if (number !== undefined) {
          result['batched_quantity.number'] = {
            ...product['quantity.number'],
            label: number,
            value: number,
          }
        } else {
          delete result['batched_quantity.number']
        }
        // Add product to the list
        acc.push(result)
        // Else single product
      } else acc.push(product)
    }
    return acc
  }, [] as AdaptedProduct[])

const prepareRotorParts = (product: AdaptedProduct): string[] => {
  const refValue = getByMetaValue(product, 'isRef')
  return refValue ? refValue.split('-') : []
}

export const sortByRotorProductID = (products: AdaptedProduct[]) =>
  products.sort((a, b) => {
    const partsA = prepareRotorParts(a)
    const partsB = prepareRotorParts(b)

    for (let i = 0; i < partsA.length; i++) {
      if ((partsA?.[i] || 0) < (partsB?.[i] || 0)) return -1
      if ((partsA?.[i] || 0) > (partsB?.[i] || 0)) return 1
    }
    return 0
  })

// Sort sections by name, except for 'Autres' (which should be last)
const getSortedSections = (
  sections: Record<string, AdaptedProduct[]>,
): Record<string, AdaptedProduct[]> => {
  const sortedSections = Object.keys(sections)
    .sort((a, b) => {
      if (a.includes('Autres')) return 1
      if (b.includes('Autres')) return -1
      return a.localeCompare(b)
    })
    ?.reduce(
      (acc, key) => {
        acc[key] = sections[key]
        return acc
      },
      {} as Record<string, AdaptedProduct[]>,
    )

  if (sortedSections['Autres'] === undefined) {
    delete sortedSections['Autres']
  }

  return sortedSections
}

const getQuantifiedSections = (
  sections: Record<string, AdaptedProduct[]>,
): SkopSection[] =>
  Object.keys(sections).map((section) => {
    const items = sections[section]
    return {
      name: section,
      quantity: getQuantities(items),
      items,
    }
  })

const getSkopSections = (products: AdaptedProduct[]) => {
  const sections = products.reduce<Record<string, AdaptedProduct[]>>(
    (acc, product) => {
      // SKOP (categories only, no header with description)
      const category = product.category?.label ?? 'Autres'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(product)
      return acc
    },
    {},
  )
  const sortedSections = getSortedSections(sections)
  return getQuantifiedSections(sortedSections)
}

const getRotorSections = ({
  products,
  context,
}: CatalogItemsParams): RotorLotSection[] => {
  const categoryOptions = context.propertyOptions?.category ?? []
  const usedLots: string[] = []
  type RotorDataStructure = Record<
    string,
    Record<string, Array<HeaderItem | AdaptedProduct>>
  >
  const dataStructure = products
    .map((product) => {
      // Product's lot
      const lot = product.lot?.value
      const lotProperty = matchKey(
        lot ?? '',
        categoryOptions,
        context.optionsByID?.category,
      )
      const lotNumber = lotProperty?.value?.toString() || 'Autres'
      // Product's category
      const category = product.category?.value
      const catProperty = matchKey(
        category ?? '',
        categoryOptions,
        context.optionsByID?.category,
      )
      const catNumber = catProperty?.value?.toString() || 'Autres'
      return {
        id: product.id,
        lotProperty,
        lotNumber,
        catProperty,
        catNumber,
      }
    })
    .sort((a, b) => {
      if (a.catNumber === 'Autres') return 1
      if (b.catNumber === 'Autres') return -1
      return a.catNumber.localeCompare(b.catNumber)
    })
    .reduce<RotorDataStructure>(
      (acc, { id, lotProperty, lotNumber, catProperty, catNumber }) => {
        const product = products.find((item) => item.id === id)
        if (!product) return acc
        if (!acc[lotNumber]) {
          acc[lotNumber] = {}
        }
        const newLot = !usedLots.includes(lotNumber)
        if (!acc[lotNumber][catNumber]) {
          const categoryKey = catProperty?.id ?? catNumber
          const lotKey = lotProperty?.id ?? lotNumber
          const header: HeaderItem = {
            id: buildSyntheticHeaderId({
              category: categoryKey,
              lot: lotKey,
            }),
            type: 'HEADER',
            ...(newLot && lotProperty?.id ? { lot: lotProperty.id } : {}),
            category: categoryKey,
          }
          acc[lotNumber][catNumber] = [header]
        }
        acc[lotNumber][catNumber].push(product)
        if (newLot) usedLots.push(lotNumber)
        return acc
      },
      {},
    )
  return Object.keys(dataStructure).map((lot) => {
    // category
    const categories = Object.keys(dataStructure[lot] ?? {}).map((category) => {
      const items = dataStructure[lot][category]
      return {
        category,
        items,
      }
    })
    // lot
    return {
      lot,
      categories,
    }
  })
}

export const setSkopPages = (
  categories: SkopSection[],
  template: OrganizationTemplate,
): SkopSection[] => {
  const itemsPerPage = getNbrOfItemsPerPage(template)
  let currentPage = getSkopSummaryPageCount(categories) + 1
  categories.forEach((category) => {
    const nbrOfItems = category.items.length
    const nbrOfPages = Math.ceil(nbrOfItems / itemsPerPage)
    category.pageNumber = currentPage
    currentPage += nbrOfPages
  })
  return categories
}

export const setRotorPages = (
  lots: RotorLotSection[],
): Array<Array<HeaderItem | AdaptedProduct>> => {
  const entries = lots.flatMap(({ categories }) =>
    categories.flatMap(({ items }) =>
      items.map((item) => {
        if (item.type === 'HEADER') {
          const header = item as HeaderItem
          return {
            item,
            type: 'HEADER' as const,
            lot: header.lot,
            heightCm: estimateItemHeight(header),
          }
        }

        return {
          item,
          type: 'PRODUCT' as const,
          heightCm: estimateItemHeight(item),
        }
      }),
    ),
  )

  return paginateRotorEntries(entries)
}

// Get the catalog pages from products
const getCatalogPages = ({ products, context }: CatalogItemsParams) => {
  const template = normalizeTemplate(context?.customSettings?.template)
  if (template === 'rotor') {
    return setRotorPages(getRotorSections({ products, context }))
  }
  return setSkopPages(getSkopSections(products), template)
}

// Get the catalog items from products, depending on the settings
export const getCatalogData = ({
  products,
  context,
  template,
}: CatalogItemsParams & { template?: string }) => {
  const reducedProducts = getReducedProducts(products)
  if (
    context?.customSettings?.generateSummary ||
    (template || context?.customSettings?.template) === 'rotor'
  )
    return getCatalogPages({ products: reducedProducts, context })
  return reducedProducts
}

// Rebuild the catalog pages with the updated items from the editor
export const getPreviewData = (blocks: CatalogBlock[], context?: Context) => {
  const normalizedBlocks = normalizeCatalogLotHeaders(blocks, context)
  const template = context?.customSettings?.template
  if (template === 'rotor') {
    return buildRotorPdfPages(normalizedBlocks)
  }
  if (!context) return normalizedBlocks

  const products = normalizedBlocks.flatMap((block) =>
    block.type === 'PRODUCT' ? [block.data] : [],
  )
  const headers = normalizedBlocks.flatMap((block) =>
    block.type === 'HEADER' ? [block.data] : [],
  )
  const pages = getCatalogPages({ products, context })
  if (pages.length === 0 || !Array.isArray(pages[0])) {
    return pages
  }
  const result = pages.map((page) =>
    page.map((item) => {
      if (item.type === 'HEADER') {
        return (
          headers.find((header) =>
            item.lot
              ? item.lot === header.lot
              : !header.lot && header.category === item.category,
          ) || item
        )
      }
      return item
    }),
  )
  return result
}

type GetRotorSummaryParams = {
  blocks: CatalogBlock[]
  context: Context
}

export type RotorSummaryItem = {
  id: string
  type: string
  value: string
  children?: RotorSummaryItem[]
}

export const getRotorSummary = ({
  blocks,
  context,
}: GetRotorSummaryParams): RotorSummaryItem[] => {
  const categoryOptions = context.propertyOptions?.category ?? []
  const headers = blocks
    .filter(({ type }) => type === 'HEADER')
    .map(({ data }) => data)

  const lots = headers
    .filter((data) => data?.lot)
    .map(({ lot }) => {
      const lotKey = extractValue(lot)
      const lotProperty = matchKey(
        lotKey,
        categoryOptions,
        context.optionsByID?.category,
      )
      const lotIndex = lotProperty?.value?.toString()
      const lotLabel = `${lotIndex}. ${lotProperty?.label}`

      const categories = headers
        .filter(({ category }) =>
          lotProperty.options?.some(
            (option) => option.id === category || option.label === category,
          ),
        )
        .map(({ category }) => {
          const categoryKey = extractValue(category)
          const catProperty = matchKey(
            categoryKey,
            categoryOptions,
            context.optionsByID?.category,
          )
          const categoryIndex = catProperty?.value?.toString().split('-')?.[0]
          const catLabel = `${categoryIndex}. ${catProperty?.label}`

          return {
            id: catProperty.id || categoryKey,
            type: 'category',
            value: catLabel.toLocaleUpperCase(),
          }
        })

      return {
        id: lotProperty.id || lotKey,
        type: 'lot',
        value: lotLabel.toLocaleUpperCase(),
        children: categories,
      }
    })

  return lots
}

export const sortBlocksByLotAndCategory = (
  blocks: CatalogBlock[],
  context?: Context,
): CatalogBlock[] => {
  if (!context) return blocks

  // 1) Build contiguous sections (header + its products)
  type RawSection = {
    header: CatalogHeaderBlock
    products: CatalogProductBlock[]
  }
  const rawSections: RawSection[] = []
  let current: RawSection | null = null
  for (const block of blocks) {
    if (block.type === 'HEADER') {
      if (current) rawSections.push(current)
      current = { header: block, products: [] }
    } else if (block.type === 'PRODUCT' && current) {
      current.products.push(block)
    }
  }
  if (current) rawSections.push(current)
  // Remove empty sections (headers without any following products)
  const nonEmptySectionsSource = rawSections.filter(
    (section) => section.products.length > 0,
  )

  // 2) Annotate each section with numeric index and lotNumber
  type Section = RawSection & { catIndex: number; lotNumber: number }
  const sections: Section[] = nonEmptySectionsSource.map((s) => {
    const { header, products } = s
    const key = extractValue(header.data.category)
    const match = matchKey(
      key,
      context.propertyOptions?.category ?? [],
      context.optionsByID?.category,
    )
    const valStr = match?.value?.toString() ?? match?.label ?? key
    let idx = parseFloat(valStr.replace(',', '.').split('-')[0])
    if (isNaN(idx)) idx = Infinity
    const lotNum = isFinite(idx) ? Math.floor(idx / 10) * 10 : Infinity
    return { header, products, catIndex: idx, lotNumber: lotNum }
  })

  // 3) Group by lotNumber
  const lotMap = new Map<number, Section[]>()
  for (const sec of sections) {
    const ln = sec.lotNumber
    if (!lotMap.has(ln)) lotMap.set(ln, [])
    lotMap.get(ln)!.push(sec)
  }

  // 4) Sort lotNumbers ascending (Infinity last)
  const lots = Array.from(lotMap.keys()).sort((a, b) =>
    a === Infinity ? 1 : b === Infinity ? -1 : a - b,
  )

  // Extract lot definitions for setting flags
  const allLots = (context.propertyOptions?.category ?? []).filter(
    (item) => item.options,
  )

  // 5) Rebuild sortedBlocks
  const sortedBlocks: CatalogBlock[] = []
  for (const lotNum of lots) {
    const group = lotMap.get(lotNum)!
    // Sort categories within lot by their index
    group.sort((a, b) =>
      a.catIndex === Infinity
        ? 1
        : b.catIndex === Infinity
          ? -1
          : a.catIndex - b.catIndex,
    )
    // Find the lot ID for this lot group (only first category will carry it)
    const lotDef =
      lotNum !== Infinity
        ? allLots.find((opt) => {
            const vs = opt.value?.toString()
            if (!vs) return false
            const num = parseFloat(vs.replace(',', '.'))
            return !isNaN(num) && num >= lotNum && num < lotNum + 10
          })
        : undefined
    // Append header + products for each category
    group.forEach((sec, idx) => {
      const hdr: CatalogHeaderBlock = {
        ...sec.header,
        data: { ...sec.header.data },
      }
      if (idx === 0 && lotDef) hdr.data.lot = lotDef.id
      else delete hdr.data.lot
      sortedBlocks.push(hdr, ...sec.products)
    })
  }

  return sortedBlocks
}

export const sanitizeHeaderBlockData = (
  data: Omit<HeaderItem, 'id'> & { id?: string },
  context?: Context,
) => {
  // Create a copy of the data to avoid mutating the original object
  const sanitizedData = { ...data }
  // Sanitize lot and category to return proper UUIDs
  const sanitizeData = (
    key: 'lot' | 'category',
    data: string | { value: string; label?: string; id?: string },
  ) => {
    const value = extractValue(data)
    if (!z.uuid().safeParse(value).success) {
      const match = matchKey(
        value,
        context?.propertyOptions?.category ?? [],
        context?.optionsByID?.category,
      )
      if (match?.id) sanitizedData[key] = match.id
    }
  }
  // Sanitize category
  if (sanitizedData.category) sanitizeData('category', sanitizedData.category)
  // Sanitize lot
  if (sanitizedData.lot) sanitizeData('lot', sanitizedData.lot)
  // Return sanitized header block data
  return { ...data, ...sanitizedData }
}

/**
 * Sanitizes catalog blocks to ensure category and lot values are proper UUIDs
 * Converts plain text labels to their corresponding IDs using matchKey
 */
export const sanitizeCatalogBlocks = (
  blocks: CatalogBlock[],
  context?: Context,
): CatalogBlock[] => {
  if (!context?.propertyOptions?.category) return blocks

  return blocks.map((block) => {
    if (block.type !== 'HEADER') return block
    return {
      ...block,
      data: {
        ...sanitizeHeaderBlockData(block.data, context),
        id: block.data.id || block.id,
      },
    }
  })
}
